import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u3gfn8l97n3eas3gl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后再试。' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费在线成网站',
    url: 'https://sczjxy.cn/',
    description: '中国领先的免费在线建站平台，提供零成本一键建站服务。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@mianfeijianzhan.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 text-sm">
                了解免费在线成网站平台的故事与使命
              </p>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 max-w-6xl mx-auto py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-5">
                品牌故事
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading leading-tight">
                让每个人都能免费在线成网站
              </h2>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  免费在线成网站平台诞生于一个简单的信念：<strong>建站不应该是少数人的特权</strong>。我们相信，无论是个人博主、小微企业，还是创业者，都应该能够轻松拥有属于自己的专业网站。
                </p>
                <p>
                  传统的建站方式门槛高、成本高，动辄数千元的设计费和复杂的编程要求，让很多人望而却步。我们的团队由一群经验丰富的互联网产品专家和设计师组成，致力于用技术的力量改变这一现状。
                </p>
                <p>
                  经过数百个日夜的研发和打磨，我们推出了真正零成本、零门槛的<strong>免费在线成网站</strong>平台。AI智能生成、海量精美模板、一键发布上线——我们让建站变得像发朋友圈一样简单。
                </p>
                <p>
                  截至目前，平台已服务超过<strong>50万用户</strong>，生成了大量优质的网站。用户的认可和信赖，是我们不断前进的最大动力。
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20diverse%20team%20collaborating%20around%20large%20screen%2C%20warm%20natural%20lighting%2C%20minimalist%20interior%20design%2C%20positive%20work%20atmosphere%2C%20professional%20tech%20company%20culture%2C%20soft%20cream%20and%20wood%20tones%2C%20editorial%20photography%20style&width=700&height=500&seq=about-brand-story&orientation=landscape"
                alt="免费在线成网站团队"
                className="w-full rounded-2xl object-cover"
              />
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                来自真实用户的美誉
              </h2>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto">
                50万+用户的信赖之选，听听他们的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 max-w-6xl mx-auto py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-5">
                联系方式
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">
                与我们取得联系
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                有任何问题或建议？我们很乐意听取你的意见。填写右侧表单，我们的团队会在24小时内回复你。
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮件</h4>
                    <p className="text-sm text-foreground-600">support@mianfeijianzhan.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-accent-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">服务时间</h4>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-answer-line text-lg text-secondary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">在线帮助</h4>
                    <p className="text-sm text-foreground-600">访问我们的帮助中心，查找常见问题的解决方案</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70"
              >
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                  发送留言
                </h3>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入你的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入你的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <div className="spam-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : (
                      '发送留言'
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-xl bg-accent-50 border border-accent-200 text-sm text-accent-800 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      {formMessage}
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {formMessage}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}